import { FormControl, FormGroup } from '@angular/forms';
import { CustomDropdownControlConfig } from 'src/app/features/reactive-forms-ui/models/custom-dropdown-control-config';
import { DateSelectorControlConfig } from 'src/app/features/reactive-forms-ui/models/date-selector-control-config';
import { FiltersPanelLayoutConfig } from 'src/app/features/reactive-forms-ui/models/filters-panel-layout-config';
import { IFormControlConfig } from 'src/app/features/reactive-forms-ui/interfaces/form-control-config';
import { FilterOptions } from '../../models/patch-management.models';

export const DEFAULT_DATA_SOURCE = 'Desktop Central';

const DATA_SOURCE_OPTIONS = [
  { name: 'OpsRamp', value: 'OpsRamp', disabled: true, disabledTooltip: 'OpsRamp data integration is coming soon' },
  { name: 'Desktop Central', value: 'Desktop Central' },
  { name: 'Both', value: 'Both', disabled: true, disabledTooltip: 'OpsRamp data integration is coming soon' },
];

const DROPDOWN_KEYS = [
  'computer',
  'patchType',
  'vendor',
  'status',
  'severity',
  'deviceType',
  'deploymentStatus',
] as const;
type DropdownKey = (typeof DROPDOWN_KEYS)[number];

const MULTISELECT_KEYS: readonly DropdownKey[] = [
  'computer',
  'patchType',
  'vendor',
  'status',
  'severity',
  'deviceType',
  'deploymentStatus',
];

export const DEFAULT_SEVERITY_FILTER = ['Critical', 'Important'];

export function toDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function fromDateString(s: string): Date {
  const [y, m, day] = s.split('-').map(Number);
  return new Date(y, m - 1, day);
}

export function createPatchFiltersForm(): FormGroup {
  return new FormGroup({
    dataSource: new FormControl<string[]>([DEFAULT_DATA_SOURCE]),
    date: new FormControl<Date | null>(null),
    computer: new FormControl<string[] | null>(null),
    patchType: new FormControl<string[] | null>(null),
    vendor: new FormControl<string[] | null>(null),
    status: new FormControl<string[] | null>(null),
    severity: new FormControl<string[] | null>(DEFAULT_SEVERITY_FILTER),
    deviceType: new FormControl<string[] | null>(null),
    deploymentStatus: new FormControl<string[] | null>(null),
  });
}

const DROPDOWN_LABELS: Record<DropdownKey, string> = {
  computer: 'Computer',
  patchType: 'Patch Type',
  vendor: 'Vendor',
  status: 'Install Status',
  severity: 'Severity',
  deviceType: 'Device Type',
  deploymentStatus: 'Deployment Status',
};

export function createPatchFilterConfigMap(): Map<string, IFormControlConfig> {
  const map = new Map<string, IFormControlConfig>([
    [
      'dataSource',
      new CustomDropdownControlConfig({
        collapsibleTitle: 'Data Source',
        reviewLabel: 'Data Source',
        label: 'Data Source',
        placeholder: 'Select data source',
        options: DATA_SOURCE_OPTIONS,
        multiselect: false,
        clearable: false,
      }),
    ],
    [
      'date',
      new DateSelectorControlConfig({
        label: 'Snapshot Date',
        collapsibleTitle: 'Snapshot Date',
        reviewLabel: 'Snapshot Date',
        todayMinMax: 'max',
      }),
    ],
  ]);

  for (const key of DROPDOWN_KEYS) {
    map.set(
      key,
      new CustomDropdownControlConfig({
        collapsibleTitle: DROPDOWN_LABELS[key],
        reviewLabel: DROPDOWN_LABELS[key],
        label: DROPDOWN_LABELS[key],
        placeholder: `All ${DROPDOWN_LABELS[key].toLowerCase()}s`,
        options: [],
        multiselect: MULTISELECT_KEYS.includes(key),
        clearable: true,
      }),
    );
  }

  return map;
}

export const PATCH_FILTERS_LAYOUT = new FiltersPanelLayoutConfig({
  elementIds: ['dataSource', 'date', ...DROPDOWN_KEYS],
  title: 'Filters',
});

export interface PatchQueryFilters {
  date?: string;
  computer?: string;
  patchType?: string;
  vendor?: string;
  status?: string;
  severity?: string;
  deviceType?: string;
  deploymentStatus?: string;
}

export function extractPatchFilters(valuesMap: any): PatchQueryFilters {
  const date = valuesMap?.date as Date | null;
  const csv = (key: DropdownKey): string | undefined => {
    const value = valuesMap?.[key];
    return Array.isArray(value) && value.length > 0 ? value.join(',') : undefined;
  };

  return {
    date: date ? toDateString(date) : undefined,
    computer: csv('computer'),
    patchType: csv('patchType'),
    vendor: csv('vendor'),
    status: csv('status'),
    severity: csv('severity'),
    deviceType: csv('deviceType'),
    deploymentStatus: csv('deploymentStatus'),
  };
}

export function applyPatchFilterOptions(
  options: FilterOptions,
  configMap: Map<string, IFormControlConfig>,
): Map<string, IFormControlConfig> {
  const optionsByKey: Record<DropdownKey, string[]> = {
    computer: options.computers ?? [],
    patchType: options.patchTypes ?? [],
    vendor: options.vendors ?? [],
    status: options.statuses ?? [],
    severity: options.severities ?? [],
    deviceType: options.deviceTypes ?? [],
    deploymentStatus: options.deploymentStatuses ?? [],
  };

  let map = configMap;
  for (const key of DROPDOWN_KEYS) {
    const existing = map.get(key) as CustomDropdownControlConfig;
    const updated = new CustomDropdownControlConfig({
      ...(existing as any),
      options: optionsByKey[key].map((value) => ({ name: value, value })),
    });
    map = new Map(map).set(key, updated);
  }

  const validDates = new Set(options.snapshotDates);
  const existingDateConfig = map.get('date') as DateSelectorControlConfig;
  const updatedDateConfig = new DateSelectorControlConfig({
    ...(existingDateConfig as any),
    dateFilterFn: (date: Date | null) => (date ? validDates.has(toDateString(date)) : false),
  });
  map = new Map(map).set('date', updatedDateConfig);

  return map;
}
